/* @jsx h */
import { h } from "vue";
import type { GridColumnDef } from "@lemmonhq/uikit";

/** Margin share of the price; below this a dish is flagged. */
export const LOW_MARGIN_PCT = 60;

/**
 * "Cost" (per portion, at the latest prices paid) and "Margin" (share of the
 * menu price incl. VAT left after cost) columns. Rows without a recipe show
 * "—" so uncosted items are easy to spot.
 */
export function costColumns<T extends { id: number }>(
  t: (key: string) => string,
  costFor: (row: T) => number | null,
  priceOf: (row: T) => number | null,
): GridColumnDef<T>[] {
  return [
    {
      key: "__cost",
      header: t("inventory.recipe.col_cost_short"),
      type: "readonly",
      width: 100,
      renderCell: (_value, row) => {
        const cost = costFor(row);
        return (
          <span class={cost == null ? "text-base-content/30" : "font-mono text-sm"}>
            {cost == null ? "—" : cost.toFixed(2)}
          </span>
        );
      },
    },
    {
      key: "__margin",
      header: t("inventory.recipe.col_margin"),
      type: "readonly",
      width: 100,
      renderCell: (_value, row) => {
        const cost = costFor(row);
        const price = priceOf(row);
        if (cost == null || !price) return <span class="text-base-content/30">—</span>;
        const margin = ((price - cost) / price) * 100;
        return (
          <span class={["text-sm font-medium", margin < LOW_MARGIN_PCT ? "text-warning" : "text-success"].join(" ")}>
            {`${margin.toFixed(0)}%`}
          </span>
        );
      },
    },
  ];
}
